import React, { useState, useEffect, useRef } from 'react';
import { Edit2, X, Type as TypeIcon, AlignLeft, AlignCenter, AlignRight, Bold, Italic, Check } from './icons';

interface TextWidgetProps {
    content: string;
    fontSize?: 'small' | 'medium' | 'large' | 'huge';
    textAlign?: 'left' | 'center' | 'right';
    textColor?: string;
    isBold?: boolean;
    isItalic?: boolean;
    onChange: (updates: { content?: string; fontSize?: any; textAlign?: any; textColor?: string; isBold?: boolean; isItalic?: boolean }) => void;
    isEditing?: boolean;
}

const COLORS = [
    { label: 'White', value: 'text-gray-200' },
    { label: 'Gray', value: 'text-gray-400' },
    { label: 'Gold', value: 'text-gold-400' },
    { label: 'Red', value: 'text-red-400' },
    { label: 'Blue', value: 'text-blue-400' },
    { label: 'Green', value: 'text-emerald-400' },
];

const SIZES = [
    { label: 'S', value: 'small' },
    { label: 'M', value: 'medium' },
    { label: 'L', value: 'large' },
    { label: 'XL', value: 'huge' },
];

export const TextWidget: React.FC<TextWidgetProps> = ({
    content,
    fontSize = 'medium',
    textAlign = 'left',
    textColor = 'text-gray-200',
    isBold = false,
    isItalic = false,
    onChange,
    isEditing: initialEditing = false
}) => {
    const [isEditing, setIsEditing] = useState(initialEditing);
    const [value, setValue] = useState(content);
    const textareaRef = useRef<HTMLTextAreaElement>(null);

    useEffect(() => {
        setValue(content);
    }, [content]);

    useEffect(() => {
        if (isEditing && textareaRef.current) {
            textareaRef.current.focus();
            textareaRef.current.style.height = 'auto';
            textareaRef.current.style.height = textareaRef.current.scrollHeight + 'px';
        }
    }, [isEditing]);

    const handleSave = () => {
        onChange({ content: value });
        setIsEditing(false);
    };

    const handleCancel = () => {
        setValue(content);
        setIsEditing(false);
    };

    const handleKeyDown = (e: React.KeyboardEvent) => {
        if (e.key === 'Enter' && !e.shiftKey) {
            e.preventDefault();
            handleSave();
        }
        if (e.key === 'Escape') {
            handleCancel();
        }
    };

    const getTextClasses = () => {
        const classes = [textColor];
        if (isBold) classes.push('font-bold');
        if (isItalic) classes.push('italic');

        switch (fontSize) {
            case 'small': classes.push('text-sm'); break;
            case 'medium': classes.push('text-base'); break;
            case 'large': classes.push('text-xl'); break;
            case 'huge': classes.push('text-3xl'); break;
        }

        switch (textAlign) {
            case 'left': classes.push('text-left'); break;
            case 'center': classes.push('text-center'); break;
            case 'right': classes.push('text-right'); break;
        }

        return classes.join(' ');
    };

    if (isEditing) {
        return (
            <div className="w-full h-full flex flex-col bg-obsidian-800 rounded-lg border border-gold-500/30 p-2 shadow-lg relative group z-10">
                <textarea
                    ref={textareaRef}
                    value={value}
                    onChange={(e) => {
                        setValue(e.target.value);
                        e.target.style.height = 'auto';
                        e.target.style.height = e.target.scrollHeight + 'px';
                    }}
                    onKeyDown={handleKeyDown}
                    className={`w-full flex-1 bg-transparent resize-none focus:outline-none leading-relaxed mb-2 min-h-0 ${getTextClasses()}`}
                    placeholder="Enter text..."
                    autoFocus
                />

                {/* Toolbar at bottom - compact, always visible, never shrinks */}
                <div
                    className="flex items-center gap-1 p-1 bg-obsidian-900/95 backdrop-blur-md rounded border border-gold-500/10 overflow-hidden h-8 flex-shrink-0 z-20 shadow-xl mx-auto"
                    style={{ minWidth: 'max-content' }}
                >
                    <div className="flex items-center gap-0.5 flex-shrink-0">
                        {SIZES.map(s => (
                            <button
                                key={s.value}
                                onClick={() => onChange({ fontSize: s.value })}
                                className={`px-1 py-0.5 text-[10px] rounded flex-shrink-0 font-medium ${fontSize === s.value ? 'bg-amber-400 text-black border border-amber-300 shadow-lg shadow-amber-400/60 font-bold' : 'text-white hover:bg-white/10'}`}
                            >
                                {s.label}
                            </button>
                        ))}
                    </div>
                    <div className="w-px h-4 bg-gray-700 flex-shrink-0" />
                    <div className="flex items-center gap-0.5 flex-shrink-0">
                        <button onClick={() => onChange({ textAlign: 'left' })} className={`p-0.5 rounded ${textAlign === 'left' ? 'bg-amber-400 text-black border border-amber-300 shadow-lg shadow-amber-400/60' : 'text-white hover:bg-white/10'}`}><AlignLeft className="w-3 h-3" /></button>
                        <button onClick={() => onChange({ textAlign: 'center' })} className={`p-0.5 rounded ${textAlign === 'center' ? 'bg-amber-400 text-black border border-amber-300 shadow-lg shadow-amber-400/60' : 'text-white hover:bg-white/10'}`}><AlignCenter className="w-3 h-3" /></button>
                        <button onClick={() => onChange({ textAlign: 'right' })} className={`p-0.5 rounded ${textAlign === 'right' ? 'bg-amber-400 text-black border border-amber-300 shadow-lg shadow-amber-400/60' : 'text-white hover:bg-white/10'}`}><AlignRight className="w-3 h-3" /></button>
                    </div>
                    <div className="w-px h-4 bg-gray-700 flex-shrink-0" />
                    <div className="flex items-center gap-0.5 flex-shrink-0">
                        <button onClick={() => onChange({ isBold: !isBold })} className={`p-0.5 rounded ${isBold ? 'bg-amber-400 text-black border border-amber-300 shadow-lg shadow-amber-400/60' : 'text-white hover:bg-white/10'}`}><Bold className="w-3 h-3" /></button>
                        <button onClick={() => onChange({ isItalic: !isItalic })} className={`p-0.5 rounded ${isItalic ? 'bg-amber-400 text-black border border-amber-300 shadow-lg shadow-amber-400/60' : 'text-white hover:bg-white/10'}`}><Italic className="w-3 h-3" /></button>
                    </div>
                    <div className="w-px h-4 bg-gray-700 flex-shrink-0" />
                    <div className="flex items-center gap-0.5 flex-shrink-0">
                        {COLORS.map(c => (
                            <button
                                key={c.value}
                                onClick={() => onChange({ textColor: c.value })}
                                className={`w-3 h-3 rounded-full border flex-shrink-0 ${textColor === c.value ? 'border-white scale-110' : 'border-transparent opacity-70 hover:opacity-100'}`}
                                style={{ backgroundColor: c.value.replace('text-', '').replace('-400', '') === 'gray-200' ? '#e5e7eb' : c.value.replace('text-', '').replace('-400', '') === 'obsidian' ? '#000' : `var(--color-${c.value.replace('text-', '').replace('-400', '')}-400)` }}
                                title={c.label}
                            >
                                <div className={`w-full h-full rounded-full ${c.value.replace('text-', 'bg-')}`} />
                            </button>
                        ))}
                    </div>
                    <div className="flex gap-1 ml-auto flex-shrink-0">
                        <button onClick={handleSave} className="p-0.5 hover:bg-emerald-500/20 text-emerald-500 rounded" title="Save"><Check className="w-3 h-3" /></button>
                        <button onClick={handleCancel} className="p-0.5 hover:bg-red-500/20 text-red-500 rounded" title="Cancel"><X className="w-3 h-3" /></button>
                    </div>
                </div>
            </div>
        );
    }

    return (
        <div
            className={`w-full h-full p-4 whitespace-pre-wrap overflow-y-auto relative group hover:bg-white/5 rounded-lg transition-colors cursor-text ${getTextClasses()}`}
            onClick={() => setIsEditing(true)}
        >
            {content || <span className="text-gray-500 italic text-base">Click to add text...</span>}
            <div className="absolute top-2 right-2 opacity-0 group-hover:opacity-100 transition-opacity bg-black/50 p-1 rounded">
                <Edit2 className="w-3 h-3 text-gray-300" />
            </div>
        </div>
    );
};
